import { useCallback } from 'react';

import type { SelectOption } from '../../inputs/Select/SelectOption/SelectOption';
import { CoreFormFieldSelect } from '../fields/CoreFormFieldSelect';
import { FormFieldContainer } from '../layout/FormFieldContainer';
import type { ContextFormFieldBaseProps } from './ContextFormFieldBaseProps';
import type { FormFieldControllerRulesProps } from './FormFieldController';
import { FormFieldController } from './FormFieldController';

export interface ContextFormFieldSelectProps<
  T,
> extends ContextFormFieldBaseProps {
  options: SelectOption<T>[];
  placeholder?: string;
  unwrapValue?: boolean;
}

export const ContextFormFieldSelect = <T,>(
  props: ContextFormFieldSelectProps<T>
) => {
  const {
    fieldPath,
    rules,
    label,
    helperText,
    colSpan,
    disabled,
    options,
    placeholder,
    unwrapValue = false,
  } = props;

  const findOption = useCallback(
    (value: T) => {
      return options.find((option) => option.value === value);
    },
    [options]
  );

  return (
    <FormFieldController
      name={fieldPath}
      rules={rules as FormFieldControllerRulesProps}
    >
      {({ field, fieldState }) => {
        const value = unwrapValue ? findOption(field.value) : field.value;
        return (
          <FormFieldContainer colSpan={colSpan}>
            <CoreFormFieldSelect
              inputProps={{ id: fieldPath, name: fieldPath }}
              label={rules?.required ? `${label} *` : label}
              options={options}
              value={value as SelectOption<T>}
              onChange={(opt) => field.onChange(unwrapValue ? opt.value : opt)}
              helperText={helperText}
              disabled={disabled}
              placeholder={placeholder}
              error={fieldState.error?.message?.toString()}
            />
          </FormFieldContainer>
        );
      }}
    </FormFieldController>
  );
};
